import { api } from './api';
import { getAccessToken } from './auth';
import type { AuditReportPayload, StatusReportPayload } from '@traaker/shared';

export interface StatusReportInput {
  brandId: string;
  windowStart?: string;
  windowEnd?: string;
  focusTaskIds?: string[];
}

/**
 * Fetch the current tenant's agency logo as a base64 data URL. The endpoint
 * is auth-protected so a plain <img src> can't reach it — we pull the bytes
 * with a bearer token and inline them. Used by both on-page preview and the
 * PDF/PPTX exporters (react-pdf's Image + pptxgenjs.addImage both accept
 * data URLs but not auth'd remote URLs).
 *
 * Returns null when no logo is uploaded — callers should fall back to a
 * text-only header.
 */
export async function fetchAgencyLogoDataUrl(): Promise<string | null> {
  const token = await getAccessToken();
  const base = (import.meta.env.VITE_API_URL as string | undefined) || '/api';
  try {
    const res = await fetch(`${base}/tenants/agency-branding/logo`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result as string);
      r.onerror = () => reject(r.error);
      r.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

export const reportsApi = {
  audit: (brandId: string) =>
    api.get<AuditReportPayload>(`/reports/audit?brandId=${encodeURIComponent(brandId)}`),

  status: (input: StatusReportInput) =>
    api.post<StatusReportPayload>('/reports/status', input),
};
